import React from 'react';
import { useGetUpcomingExpirationsQuery } from '../../store/api/superAdminApi';
import { 
  ExclamationTriangleIcon, 
  CalendarDaysIcon,
  BuildingOfficeIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';

const SubscriptionReminders = () => {
  const { data: upcomingExpirations, isLoading, error } = useGetUpcomingExpirationsQuery();

  // Debug: Log the data structure
  console.log('Upcoming expirations data:', upcomingExpirations);

  const getDaysRemaining = (endDate) => {
    if (!endDate) return -999; // Return a very negative number for missing dates
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getPriorityClass = (daysRemaining) => {
    if (daysRemaining <= 1) return 'border-red-500 bg-red-50';
    if (daysRemaining <= 3) return 'border-orange-500 bg-orange-50';
    return 'border-yellow-500 bg-yellow-50';
  };

  const getPriorityIcon = (daysRemaining) => {
    if (daysRemaining <= 1) return 'text-red-600';
    if (daysRemaining <= 3) return 'text-orange-600';
    return 'text-yellow-600';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-soft border border-secondary-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-secondary-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-secondary-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-soft border border-secondary-200 p-6">
        <div className="text-center text-red-600">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-2" />
          <p>Failed to load subscription reminders</p>
        </div>
      </div>
    );
  }

  const expiredCompanies = (upcomingExpirations?.data || upcomingExpirations || []).filter(company => {
    if (!company.subscription?.endDate) return false;
    return getDaysRemaining(company.subscription.endDate) < 0;
  });
  
  const expiringCompanies = (upcomingExpirations?.data || upcomingExpirations || []).filter(company => {
    if (!company.subscription?.endDate) return false;
    const days = getDaysRemaining(company.subscription.endDate);
    return days >= 0 && days <= 7;
  });

  if (expiredCompanies.length === 0 && expiringCompanies.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-soft border border-secondary-200 p-6">
        <div className="flex items-center mb-4">
          <CalendarDaysIcon className="h-6 w-6 text-green-600 mr-3" />
          <h3 className="text-lg font-semibold text-secondary-900">Subscription Reminders</h3>
        </div>
        <div className="text-center py-8">
          <CalendarDaysIcon className="h-16 w-16 text-green-500 mx-auto mb-4 opacity-50" />
          <p className="text-secondary-600">No upcoming subscription expirations</p>
          <p className="text-sm text-secondary-500 mt-1">All companies have active subscriptions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-secondary-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-6 w-6 text-orange-600 mr-3" />
          <h3 className="text-lg font-semibold text-secondary-900">Subscription Reminders</h3>
        </div>
        <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
          {expiredCompanies.length + expiringCompanies.length} Alert{(expiredCompanies.length + expiringCompanies.length) !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-4">
        {/* Expired Subscriptions */}
        {expiredCompanies.map((company) => {
          if (!company.subscription?.endDate) return null;
          const daysOverdue = Math.abs(getDaysRemaining(company.subscription.endDate));
          return (
            <div 
              key={`expired-${company._id}`}
              className="border-2 border-red-500 bg-red-50 rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <BuildingOfficeIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-red-900 truncate">
                      {company.name || 'Unknown Company'}
                    </h4>
                    <p className="text-xs text-red-700 mt-1">
                      Plan: {company.subscription?.plan?.name || 'Unknown Plan'}
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      Expired: {formatDate(company.subscription.endDate)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
                  </span>
                </div>
              </div>
            </div>
          );
        }).filter(Boolean)}

        {/* Expiring Soon */}
        {expiringCompanies.map((company) => {
          if (!company.subscription?.endDate) return null;
          const daysRemaining = getDaysRemaining(company.subscription.endDate);
          return (
            <div 
              key={`expiring-${company._id}`}
              className={`border-2 rounded-xl p-4 ${getPriorityClass(daysRemaining)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <BuildingOfficeIcon className={`h-6 w-6 ${getPriorityIcon(daysRemaining)}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-sm font-semibold truncate ${
                      daysRemaining <= 1 ? 'text-red-900' : 
                      daysRemaining <= 3 ? 'text-orange-900' : 'text-yellow-900'
                    }`}>
                      {company.name || 'Unknown Company'}
                    </h4>
                    <p className={`text-xs mt-1 ${
                      daysRemaining <= 1 ? 'text-red-700' : 
                      daysRemaining <= 3 ? 'text-orange-700' : 'text-yellow-700'
                    }`}>
                      Plan: {company.subscription?.plan?.name || 'Unknown Plan'}
                    </p>
                    <p className={`text-xs mt-1 ${
                      daysRemaining <= 1 ? 'text-red-600' : 
                      daysRemaining <= 3 ? 'text-orange-600' : 'text-yellow-600'
                    }`}>
                      Expires: {formatDate(company.subscription.endDate)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    daysRemaining <= 1 ? 'bg-red-100 text-red-800' : 
                    daysRemaining <= 3 ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                  </span>
                </div>
              </div>
            </div>
          );
        }).filter(Boolean)}
      </div>

      {/* Action Button */}
      {(expiredCompanies.length > 0 || expiringCompanies.length > 0) && (
        <div className="mt-6 pt-4 border-t border-secondary-200">
          <button
            onClick={() => window.location.href = '/super-admin/companies'}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Manage Company Subscriptions
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionReminders;